import apiClient from "./apiClient";
import i18n from "i18next";

const GB_ORDERS_BASE = process.env.REACT_APP_URL_BALLOONS_SERVICE;

type SortDirection = "ASC" | "DESC";
export type GasBalloonOrderFileType = "PDF" | "XLSX";

interface LooseObject {
  [key: string]: any;
}

const langHeaders = () => ({
  "Content-Type": "application/json",
  "Accept-Language": i18n.language?.includes("ru") ? "ru-RU" : "kk-KZ",
});

export const getGasBalloonOrdersList = async (
  page: number = 1,
  size: number = 10,
  sortDirection: SortDirection = "DESC",
  startDate?: string,
  endDate?: string,
  number?: string,
  status?: string[],
  orgIinBin?: string | number,
  codesAmount?: number,
): Promise<any> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortDirection,
  }).toString();

  const body: LooseObject = {};
  if (startDate) body.creationDateStart = startDate;
  if (endDate) body.creationDateEnd = endDate;
  if (number) body.number = number;
  if (status && status.length) body.status = status;
  if (orgIinBin) body.orgIinBin = String(orgIinBin);
  if (codesAmount) body.codesAmount = codesAmount;

  const response = await apiClient.post(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/list?${params}`,
    body,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const createGasBalloonOrder = async (orderData: {
  codesAmount: number;
  note?: string | null;
}): Promise<any> => {
  const response = await apiClient.post(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/create`,
    orderData,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const editGasBalloonOrder = async (
  orderId: number | string,
  orderData: { codesAmount: number; note?: string | null },
): Promise<any> => {
  const response = await apiClient.put(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/edit/${orderId}`,
    orderData,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const deleteGasBalloonOrder = async (orderId: number | string): Promise<any> => {
  const response = await apiClient.delete(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/delete/${orderId}`,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const searchGasBalloonOrderById = async (id: number | string): Promise<any> => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/search-by-id/${id}`,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const getGasBalloonOrderCodes = async (
  orderId: number | string,
  page: number = 1,
  size: number = 10,
  sortDirection: SortDirection = "ASC",
): Promise<any> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortDirection,
  }).toString();

  const response = await apiClient.get(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/codes/list/${orderId}?${params}`,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const getGasBalloonOrderXml = async (orderId: number | string): Promise<any> => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/get/xml/${orderId}`,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const signGasBalloonOrder = async (
  orderId: number | string,
  signedXML: string,
): Promise<any> => {
  const response = await apiClient.post(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/sign/${orderId}`,
    { signedXML },
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};

export const downloadGasBalloonOrderFile = async (
  orderId: number | string,
  fileType: GasBalloonOrderFileType,
) => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/download/${orderId}/${fileType}`,
    { headers: langHeaders(), withCredentials: true, responseType: "blob" },
  );
  return response.data;
};

export const activateGasBalloonCode = async (payload: {
  code: string;
  balloonId?: number | null;
}): Promise<any> => {
  const response = await apiClient.post(
    `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/orders/codes/activate`,
    payload,
    { headers: langHeaders(), withCredentials: true },
  );
  return response.data;
};
